import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import { anteprimaAutenticata, downloadAutenticato } from '../utils/downloadFile';
import { getUserRole } from '../utils/auth';
import { toast } from 'react-toastify';
import { 
  ArrowLeftIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  UserIcon,
  BuildingOfficeIcon,
  InformationCircleIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const GestioneDocumentiPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [pratica, setPratica] = useState(null);
  const [documenti, setDocumenti] = useState([]);
  const [preventivi, setPreventivi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ruoloUtente, setRuoloUtente] = useState(null);
  const [documentiRichiesti, setDocumentiRichiesti] = useState([]);
  const [preventivoApprovato, setPreventivoApprovato] = useState(null);
  const [validazioneDocumenti, setValidazioneDocumenti] = useState({});
  const [noteValidazione, setNoteValidazione] = useState({});

  useEffect(() => {
    const ruolo = getUserRole();
    setRuoloUtente(ruolo);
    
    // Verifica che l'utente sia admin
    if (ruolo !== 'admin') {
      toast.error('Accesso negato: solo gli amministratori possono accedere a questa pagina');
      navigate('/nlt/pratiche');
      return;
    }
    
    if (id) {
      fetchDatiPratica();
    }
  }, [id, navigate]);

  const fetchDatiPratica = async () => {
    setLoading(true);
    try {
      // Carica dati pratica
      const praticaResponse = await api.get(`/pratica/${id}`);
      setPratica(praticaResponse.data);

      // Carica preventivi
      const preventiviResponse = await api.get(`/pratica/${id}/preventivi`);
      setPreventivi(preventiviResponse.data);

      // Carica documenti
      const documentiResponse = await api.get(`/pratica/${id}/documenti`);
      setDocumenti(documentiResponse.data);

      // Trova il preventivo approvato
      const preventivoApprovato = preventiviResponse.data.find(p => p.statoPreventivo === 'accettato');
      setPreventivoApprovato(preventivoApprovato);

      // Carica documenti richiesti dall'API unificata
      const documentiRichiestiResponse = await api.get(`/pratica/${id}/documenti-richiesti`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (documentiRichiestiResponse.data.success) {
        const documentiNecessari = documentiRichiestiResponse.data.documentiRichiesti.map(doc => doc.descrizione);
        setDocumentiRichiesti(documentiNecessari);

        // Inizializza validazione documenti
        const validazioneIniziale = {};
        const noteIniziali = {};
        documentiNecessari.forEach(doc => {
          validazioneIniziale[doc] = 'pending'; // pending, approved, rejected
          noteIniziali[doc] = '';
        });
        setValidazioneDocumenti(validazioneIniziale);
        setNoteValidazione(noteIniziali);
      }

    } catch (error) {
      console.error('Errore nel caricamento dati:', error);
      setError('Errore nel caricamento dei dati della pratica');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatoDocumento = (nomeDocumento) => {
    // Verifica se il documento è stato caricato
    const documentoCaricato = documenti.find(doc => 
      doc.descrizione?.toLowerCase().includes(nomeDocumento.toLowerCase().split(' ')[0]) ||
      doc.nome_file?.toLowerCase().includes(nomeDocumento.toLowerCase().split(' ')[0])
    );

    if (!documentoCaricato) {
      return { stato: 'mancante', documento: null };
    }

    const validazione = validazioneDocumenti[nomeDocumento];
    return { 
      stato: validazione || 'pending', 
      documento: documentoCaricato 
    };
  };

  const aggiornaValidazioneDocumento = async (nomeDocumento, nuovoStato, note = '') => {
    try {
      setValidazioneDocumenti(prev => ({
        ...prev,
        [nomeDocumento]: nuovoStato
      }));

      setNoteValidazione(prev => ({
        ...prev,
        [nomeDocumento]: note
      }));

      // Qui potresti salvare la validazione nel database
      // await api.put(`/pratica/${id}/documenti/validazione`, {
      //   documento: nomeDocumento,
      //   stato: nuovoStato,
      //   note: note
      // });

      toast.success(`Validazione documento aggiornata: ${nomeDocumento}`);
    } catch (error) {
      console.error('Errore nell\'aggiornamento validazione:', error);
      toast.error('Errore nell\'aggiornamento della validazione');
    }
  };

  const getIconaStato = (stato) => {
    switch (stato) {
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XMarkIcon className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'mancante':
        return <XMarkIcon className="h-5 w-5 text-gray-400" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getColoreStato = (stato) => {
    switch (stato) {
      case 'approved':
        return 'bg-green-50 border-green-200';
      case 'rejected':
        return 'bg-red-50 border-red-200';
      case 'pending':
        return 'bg-yellow-50 border-yellow-200';
      case 'mancante':
        return 'bg-gray-50 border-gray-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const calcolaStatisticheDocumenti = () => {
    const totale = documentiRichiesti.length;
    let approvati = 0;
    let rifiutati = 0;
    let inRevisione = 0;
    let mancanti = 0;

    documentiRichiesti.forEach(doc => {
      const { stato } = getStatoDocumento(doc);
      switch (stato) {
        case 'approved':
          approvati++;
          break;
        case 'rejected':
          rifiutati++;
          break;
        case 'pending':
          inRevisione++;
          break;
        case 'mancante':
          mancanti++;
          break;
      }
    });

    return { totale, approvati, rifiutati, inRevisione, mancanti };
  };

  const completaValidazioneDocumenti = async () => {
    const stats = calcolaStatisticheDocumenti();
    
    if (stats.mancanti > 0) {
      toast.error(`Ci sono ancora ${stats.mancanti} documenti mancanti`);
      return;
    }

    if (stats.rifiutati > 0) {
      toast.error(`Ci sono ${stats.rifiutati} documenti rifiutati che devono essere ricaricati`);
      return;
    }

    if (stats.inRevisione > 0) {
      toast.error(`Ci sono ancora ${stats.inRevisione} documenti in attesa di validazione`);
      return;
    }

    try {
      // Avanza lo stato della pratica
      await api.put(`/pratica/${id}/stato`, {
        stato: 'pratica_in_valutazione',
        note: 'Tutti i documenti sono stati validati e approvati'
      });

      toast.success('Validazione documenti completata! La pratica è ora in valutazione.');
      navigate(`/nlt/pratiche/${id}`);
    } catch (error) {
      console.error('Errore nel completamento validazione:', error);
      toast.error('Errore nel completamento della validazione');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-500">Caricamento gestione documenti...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Errore</h2>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/nlt/pratiche')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Torna alle Pratiche
          </button>
        </div>
      </div>
    );
  }

  if (!pratica) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Pratica non trovata</h2>
          <p className="text-gray-500 mb-4">La pratica richiesta non esiste o non è accessibile.</p>
          <button
            onClick={() => navigate('/nlt/pratiche')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Torna alle Pratiche
          </button>
        </div>
      </div>
    );
  }

  const stats = calcolaStatisticheDocumenti();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="w-full px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/nlt/pratiche/${id}`)}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Torna alla Pratica
              </button>
              <div className="h-6 border-l border-gray-300"></div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestione Documenti</h1>
                <p className="text-sm text-gray-600">
                  Pratica #{pratica.codicePratica} - {pratica.cliente?.ragione_sociale || 
                   `${pratica.cliente?.nome || ''} ${pratica.cliente?.cognome || ''}`.trim()}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-gray-500">Stato Pratica</div>
                <div className="font-medium text-gray-900">{pratica.statoPratica}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenuto Principale */}
      <div className="w-full px-6 py-6">
        
        {/* Statistiche Documenti */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center">
              <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Totale</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totale}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Approvati</p>
                <p className="text-2xl font-semibold text-green-600">{stats.approvati}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">In Revisione</p>
                <p className="text-2xl font-semibold text-yellow-600">{stats.inRevisione}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center">
              <XMarkIcon className="h-8 w-8 text-red-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Rifiutati</p>
                <p className="text-2xl font-semibold text-red-600">{stats.rifiutati}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-gray-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Mancanti</p>
                <p className="text-2xl font-semibold text-gray-600">{stats.mancanti}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Informazioni Cliente e Preventivo */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          
          {/* Info Cliente */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                Informazioni Cliente
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Tipo Cliente:</span>
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {pratica.cliente?.tipo_cliente || 'Non specificato'}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Nome/Ragione Sociale:</span>
                  <p className="text-sm font-medium text-gray-900">
                    {pratica.cliente?.ragione_sociale || 
                     `${pratica.cliente?.nome || ''} ${pratica.cliente?.cognome || ''}`.trim() ||
                     'Non specificato'}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Email:</span>
                  <p className="text-sm font-medium text-gray-900">
                    {pratica.cliente?.email || 'Non specificata'}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Telefono:</span>
                  <p className="text-sm font-medium text-gray-900">
                    {pratica.cliente?.telefono || 'Non specificato'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Info Preventivo Approvato */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 mr-2 text-green-600" />
                Preventivo Approvato
              </h3>
            </div>
            <div className="p-6">
              {preventivoApprovato ? (
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Società di Noleggio:</span>
                    <p className="text-sm font-medium text-gray-900">
                      {(() => {
                        const datiOfferta = typeof preventivoApprovato.datiOfferta === 'string' 
                          ? JSON.parse(preventivoApprovato.datiOfferta) 
                          : preventivoApprovato.datiOfferta || {};
                        return datiOfferta.societaNoleggio?.nome || datiOfferta.societaNoleggio || 'Non specificata';
                      })()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Vettura:</span>
                    <p className="text-sm font-medium text-gray-900">
                      {preventivoApprovato.vettura?.brand} {preventivoApprovato.vettura?.modello}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Data Approvazione:</span>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(preventivoApprovato.dataUltimaModifica)}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <InformationCircleIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Nessun preventivo approvato trovato</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Lista Documenti Richiesti */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b bg-gray-50">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-blue-600" />
                Documenti Richiesti ({documentiRichiesti.length})
              </h3>
              
              {stats.totale > 0 && stats.approvati === stats.totale && (
                <button
                  onClick={completaValidazioneDocumenti}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Completa Validazione
                </button>
              )}
            </div>
          </div>
          
          <div className="p-6">
            {documentiRichiesti.length === 0 ? (
              <div className="text-center py-8">
                <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessun documento richiesto</h3>
                <p className="text-gray-600">
                  Non sono stati definiti documenti specifici per questo tipo di cliente e società di noleggio.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {documentiRichiesti.map((documento, index) => {
                  const { stato, documento: docCaricato } = getStatoDocumento(documento);
                  
                  return (
                    <div key={index} className={`border rounded-lg p-4 ${getColoreStato(stato)}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          {getIconaStato(stato)}
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{documento}</h4>
                            
                            {docCaricato && (
                              <div className="mt-2 text-sm text-gray-600">
                                <p><strong>File:</strong> {docCaricato.nome_file}</p>
                                <p><strong>Caricato il:</strong> {formatDate(docCaricato.data_caricamento)}</p>
                                <p><strong>Dimensione:</strong> {(docCaricato.dimensione_file / 1024).toFixed(1)} KB</p>
                              </div>
                            )}
                            
                            {noteValidazione[documento] && (
                              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                <p className="text-sm text-blue-800">
                                  <strong>Note:</strong> {noteValidazione[documento]}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-4">
                          {docCaricato && (
                            <>
                              <button
                                onClick={() => anteprimaAutenticata(`/api/pratica/${id}/documenti/${docCaricato.id}/download`).catch((e) => toast.error(e.message || 'Errore nell\'anteprima del documento'))}
                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center"
                              >
                                <EyeIcon className="h-4 w-4 mr-1" />
                                Visualizza
                              </button>

                              <button
                                onClick={() => downloadAutenticato(`/api/pratica/${id}/documenti/${docCaricato.id}/download`, docCaricato.nome_file).catch((e) => toast.error(e.message || 'Errore nel download del documento'))}
                                className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 flex items-center"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                                Scarica
                              </button>

                              {stato === 'pending' && (
                                <>
                                  <button
                                    onClick={() => {
                                      const note = prompt('Inserisci eventuali note per l\'approvazione:');
                                      aggiornaValidazioneDocumento(documento, 'approved', note || '');
                                    }}
                                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center"
                                  >
                                    <CheckIcon className="h-4 w-4 mr-1" />
                                    Approva
                                  </button>
                                  
                                  <button
                                    onClick={() => {
                                      const note = prompt('Inserisci il motivo del rifiuto:');
                                      if (note) {
                                        aggiornaValidazioneDocumento(documento, 'rejected', note);
                                      }
                                    }}
                                    className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center"
                                  >
                                    <XMarkIcon className="h-4 w-4 mr-1" />
                                    Rifiuta
                                  </button>
                                </>
                              )}
                              
                              {stato === 'approved' && (
                                <button
                                  onClick={() => aggiornaValidazioneDocumento(documento, 'pending', '')}
                                  className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                                >
                                  Rivedi
                                </button>
                              )}
                              
                              {stato === 'rejected' && (
                                <button
                                  onClick={() => aggiornaValidazioneDocumento(documento, 'pending', '')}
                                  className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                                >
                                  Rivedi
                                </button>
                              )}
                            </>
                          )}
                          
                          {!docCaricato && (
                            <span className="px-3 py-1 bg-gray-200 text-gray-600 rounded text-sm">
                              Non caricato
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GestioneDocumentiPage; 